<script setup lang="ts">
import { watch } from 'vue';
import { useForm } from 'vee-validate';
import { trustSchema } from '@/validation';
import { TrusteeTypeOptions, TrustStructureOptions } from '@/helpers/const';
import type { TrustDetails } from '@/types';

const props = defineProps<{
  modelValue: TrustDetails;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: TrustDetails): void;
}>();

const schema = trustSchema();

const { meta, submitCount, values, handleSubmit, resetForm } = useForm<TrustDetails>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: { ...props.modelValue },
});

const update = handleSubmit(
  (formValues) => {
    emit('update:modelValue', { ...formValues });
    return true;
  },
  () => false,
);

const cancel = () => {
  resetForm({ values: { ...props.modelValue } });
};

watch(
  () => props.modelValue,
  (trust) => {
    if (trust) {
      resetForm({ values: { ...trust } });
    }
  },
);

defineExpose({ update, cancel, resetForm, meta, values });
</script>

<template>
  <form>
    <div>
      <BxField v-slot="{ field }" name="trustName">
        <BxInput v-bind="field" id="trust-name" class="form-control" label="Trust Name" label-type="floating" />
      </BxField>

      <div class="form-group mt-6">
        <label class="mb-2 block text-sm font-medium text-gray-700">Is the trustee/s an individual or company?</label>
        <BxField v-slot="{ field }" name="trusteeType">
          <BxSelect
            v-bind="field"
            id="trust-trustee-type"
            :list-items="TrusteeTypeOptions"
            class="form-control !mt-0"
            label="Trustee Type"
            label-type="floating"
            placeholder="Select trustee type..."
          />
        </BxField>
      </div>

      <div class="form-group mt-6">
        <label class="mb-2 block text-sm font-medium text-gray-700">What is the trust structure?</label>
        <BxField v-slot="{ field }" name="trustStructure">
          <BxSelect
            v-bind="field"
            id="trust-trust-structure"
            :list-items="TrustStructureOptions"
            class="form-control !mt-0"
            label="Trust Structure"
            label-type="floating"
            placeholder="Select trust structure..."
          />
        </BxField>
      </div>

      <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
        Please review the error messages above.
      </BxErrorPanel>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.form-control {
  margin-top: 1.5rem;
}
</style>
